import { FiBookOpen } from 'react-icons/fi';

export const academicLecturesData = {
  courseId: 'academic-lectures',
  courseType: 'listening-course',
  title: 'Academic Lectures',
  icon: FiBookOpen,
  overview: {
    impact: 'Master Section 4 — the most challenging listening section',
    focus: 'Following extended academic monologues, signpost language, and note-taking',
    uses: 'Listening Section 4',
    duration: '25-30 minutes',
  },
  sections: [
    {
      title: 'Understanding Section 4',
      content: `<p>Section 4 is the final and most difficult part of the IELTS Listening test. It features a <strong>single speaker</strong> delivering a university-style lecture or talk on an academic topic. Unlike earlier sections, there is <em>no pause</em> in the middle — the audio runs straight through.</p>
<p>Topics range from natural sciences and history to business and social studies. You don't need specialist knowledge, but you must follow the <strong>structure</strong> of the lecture and recognise when the speaker moves between points.</p>`,
      points: [
        'Monologue format: one speaker, no conversational cues to help you',
        'No break in the middle — you must maintain focus for 4-5 minutes straight',
        'Questions follow the order of the audio, so track your position carefully',
        'Topics are academic but accessible — specific subject knowledge is not required',
        'Expect 10 questions, typically a mix of sentence completion, summary completion, and multiple choice',
      ],
    },
    {
      title: 'Signpost Language',
      content: `<p>Signpost language is how speakers organise their talk. Recognising these phrases tells you <strong>where</strong> you are in the lecture and what kind of information is coming next. This is your navigation system.</p>`,
      table: {
        headers: ['Function', 'Signpost Phrases'],
        rows: [
          ['Introducing the topic', '"Today I\'m going to talk about...", "The focus of this lecture is...", "I\'d like to begin by..."'],
          ['Moving to a new point', '"Now I\'d like to turn to...", "Moving on to...", "Let\'s now consider...", "The next point is..."'],
          ['Giving an example', '"For instance...", "A good example of this is...", "To illustrate this...", "Take the case of..."'],
          ['Emphasising', '"It\'s important to note that...", "The key point here is...", "What\'s particularly significant is...", "I want to stress that..."'],
          ['Contrasting', '"On the other hand...", "However...", "In contrast to this...", "Unlike the previous example..."'],
          ['Adding information', '"In addition to this...", "Furthermore...", "Another factor is...", "What\'s more..."'],
          ['Giving a reason', '"This is because...", "The reason for this is...", "This is due to...", "One explanation is..."'],
          ['Summarising', '"To sum up...", "In conclusion...", "So, to summarise...", "The main takeaway is..."'],
          ['Referring back', '"As I mentioned earlier...", "Going back to what I said about...", "If you recall..."'],
          ['Expressing opinion', '"I would argue that...", "In my view...", "The evidence suggests..."'],
        ],
      },
    },
    {
      title: 'Academic Vocabulary Strategies',
      content: `<p>Academic lectures use formal vocabulary that can feel unfamiliar. However, speakers almost always <strong>explain or rephrase</strong> difficult terms. The key is knowing how to handle words you don't initially understand.</p>`,
      points: [
        '<strong>Don\'t panic over unknown words:</strong> The speaker usually paraphrases or defines them — wait 2-3 seconds',
        '<strong>Listen for definitions:</strong> "By X, I mean..." or "X — that is..." signals an explanation is coming',
        '<strong>Use word parts:</strong> Prefixes (bio-, geo-, micro-) and suffixes (-tion, -ment, -ology) reveal meaning',
        '<strong>Context helps enormously:</strong> Even if you don\'t know "biodiversity", the surrounding words ("species", "ecosystem", "plants and animals") make the meaning clear',
        '<strong>Build academic word lists:</strong> The Academic Word List (AWL) covers vocabulary common across university subjects',
        '<strong>Practise with TED Talks and podcasts:</strong> Exposure to academic English builds familiarity naturally',
      ],
    },
    {
      title: 'Identifying Main Ideas vs Details',
      content: `<p>A common trap in Section 4 is confusing <strong>main ideas</strong> with <strong>supporting details</strong>. Questions may ask you to complete a summary (main ideas) or fill in specific facts (details). You need to distinguish between the two.</p>`,
      points: [
        '<strong>Main ideas</strong> are signalled by emphasis: "The crucial finding was...", "The main reason is..."',
        '<strong>Details</strong> follow main ideas as examples, statistics, or specific cases',
        '<strong>Summary questions</strong> test main ideas — listen for the overall argument, not individual facts',
        '<strong>Gap-fill questions</strong> often test specific details — listen for precise names, numbers, and terms',
        'The speaker\'s <strong>tone</strong> changes when stating something important vs giving a minor example',
      ],
      warning: 'Don\'t write the first thing you hear. In academic lectures, the speaker often builds up to the answer with context before stating the key information.',
    },
  ],
  exercises: [
    {
      title: 'Identify Signpost Function',
      type: 'select',
      instructions: 'What function does each signpost phrase serve in a lecture?',
      questions: [
        {
          text: '"Now I\'d like to turn to the economic implications."',
          options: ['Introducing a new topic', 'Giving an example', 'Summarising'],
          answer: 'Introducing a new topic',
          explanation: '"I\'d like to turn to" is a classic transition phrase that signals a shift to a new subtopic.',
        },
        {
          text: '"It\'s important to note that this only applies to freshwater species."',
          options: ['Emphasising a key point', 'Contrasting ideas', 'Moving to a new point'],
          answer: 'Emphasising a key point',
          explanation: '"It\'s important to note" signals that the speaker is highlighting something crucial — often an answer!',
        },
        {
          text: '"A good example of this is the 2010 Chilean mining rescue."',
          options: ['Giving an example', 'Summarising', 'Expressing opinion'],
          answer: 'Giving an example',
          explanation: '"A good example of this is" directly introduces a specific illustrative case.',
        },
        {
          text: '"So, to summarise, the three main factors were cost, location, and accessibility."',
          options: ['Summarising', 'Adding information', 'Giving a reason'],
          answer: 'Summarising',
          explanation: '"So, to summarise" signals the speaker is wrapping up and restating the main points concisely.',
        },
      ],
    },
    {
      title: 'Match Signpost Phrases to Meaning',
      type: 'select',
      instructions: 'Choose what the speaker is about to do after each phrase.',
      questions: [
        {
          text: '"This is due to several interconnected factors."',
          options: ['Explain causes/reasons', 'Give a conclusion', 'Introduce a new topic'],
          answer: 'Explain causes/reasons',
          explanation: '"Due to" signals that reasons or causes will follow. Listen for a list of factors.',
        },
        {
          text: '"In contrast to the European model..."',
          options: ['Present a different approach', 'Repeat the same idea', 'Summarise the lecture'],
          answer: 'Present a different approach',
          explanation: '"In contrast to" explicitly signals that the next information will differ from what was just discussed.',
        },
        {
          text: '"As I mentioned earlier in relation to climate patterns..."',
          options: ['Refer back to a previous point', 'Introduce new research', 'Give a definition'],
          answer: 'Refer back to a previous point',
          explanation: '"As I mentioned earlier" links back to something already discussed — helpful for tracking lecture flow.',
        },
        {
          text: '"Furthermore, recent studies have shown that..."',
          options: ['Add supporting information', 'Contradict the previous point', 'End the lecture'],
          answer: 'Add supporting information',
          explanation: '"Furthermore" adds to the current argument with additional evidence or points.',
        },
      ],
    },
    {
      title: 'Distinguish Main Idea from Detail',
      type: 'select',
      instructions: 'Is each statement a main idea or a supporting detail?',
      questions: [
        {
          text: '"The key finding of the study was that urban green spaces significantly reduce stress levels."',
          options: ['Main idea', 'Supporting detail'],
          answer: 'Main idea',
          explanation: '"The key finding" explicitly marks this as the central point. This is what a summary question would test.',
        },
        {
          text: '"For example, participants who walked through parks for 20 minutes showed a 15% reduction in cortisol."',
          options: ['Supporting detail', 'Main idea'],
          answer: 'Supporting detail',
          explanation: '"For example" and the specific statistics (20 minutes, 15%) mark this as a detail supporting the main finding.',
        },
        {
          text: '"The research suggests three primary benefits of bilingual education."',
          options: ['Main idea', 'Supporting detail'],
          answer: 'Main idea',
          explanation: 'This is an overarching claim that frames the rest of the discussion. It establishes the three-part structure.',
        },
        {
          text: '"In one school in Barcelona, test scores improved by 12% after implementing the bilingual programme."',
          options: ['Supporting detail', 'Main idea'],
          answer: 'Supporting detail',
          explanation: '"In one school" and the specific percentage are evidence supporting the broader claim about bilingual education.',
        },
      ],
    },
    {
      title: 'Complete Lecture Notes',
      type: 'fill-in',
      instructions: 'Fill in the missing word that would logically complete these lecture notes.',
      questions: [
        {
          text: 'Lecture topic: Marine Biology. "The main threat to coral reefs is rising ocean ___."',
          answer: 'temperature',
          explanation: 'In marine biology, rising ocean temperature (due to climate change) is the primary threat to coral reefs — a well-established academic topic.',
        },
        {
          text: 'Lecture on urban planning: "The speaker argues that public ___ is more efficient than private cars in reducing congestion."',
          answer: 'transport',
          explanation: '"Public transport" (or "transportation") is the standard academic term contrasted with private vehicles in urban planning discussions.',
        },
        {
          text: 'Lecture on psychology: "The study found that sleep ___ negatively affects both memory and concentration."',
          answer: 'deprivation',
          explanation: '"Sleep deprivation" is the academic term for insufficient sleep. The negative effects on memory and concentration are a classic research finding.',
        },
      ],
    },
    {
      title: 'Summarise a Lecture Segment',
      type: 'writing',
      instructions: 'Read the lecture excerpt and write a concise summary identifying the main idea and key supporting points.',
      questions: [
        {
          text: 'Excerpt: "Now I\'d like to turn to renewable energy. The key advantage is sustainability — unlike fossil fuels, solar and wind power don\'t deplete natural resources. Furthermore, the cost of solar panels has dropped by 90% since 2010. However, one challenge remains: energy storage. Current battery technology cannot efficiently store large amounts of energy for use when the sun isn\'t shining."',
          example: 'Main idea: Renewable energy is sustainable but faces storage challenges. Key points: (1) Solar and wind don\'t deplete resources (advantage), (2) Solar panel costs dropped 90% since 2010 (supporting evidence), (3) Energy storage remains a challenge (limitation). Signpost language used: "I\'d like to turn to" (topic shift), "Furthermore" (adding info), "However" (contrast).',
          tip: 'Identify the main argument first, then list 2-3 supporting points. Note any signpost phrases that helped you follow the structure.',
        },
        {
          text: 'Excerpt: "The research I want to focus on today examined the effects of noise pollution on learning. It\'s important to note that children in schools near airports scored significantly lower on reading tests. A good example of this is a study in Munich where, after the old airport closed and a new one opened, reading scores improved near the old airport and declined near the new one."',
          example: 'Main idea: Noise pollution from airports negatively affects children\'s reading ability. Key evidence: Munich airport study — when the old airport closed, nearby schools\' reading scores improved; when the new airport opened, nearby schools\' scores declined. This natural experiment provides strong causal evidence. Signpost: "It\'s important to note" (emphasis), "A good example" (illustration).',
          tip: 'Look for cause-and-effect relationships. The Munich study is a supporting detail that proves the main idea.',
        },
      ],
    },
  ],
  tips: [
    'Use the preview time to read ALL Section 4 questions — there\'s no break in the middle',
    'Underline keywords in questions so you can track where you are as the lecture progresses',
    'Listen for signpost language — it tells you when a new answer is approaching',
    'Don\'t stop writing if you miss an answer — move on and catch the next one',
  ],
  dailyPractice: [
    'Listen to one TED Talk or academic podcast daily and note the signpost phrases used',
    'Practise note-taking: summarise a 5-minute lecture into bullet points while listening',
    'Build academic vocabulary by learning 5 new AWL words per day with example sentences',
    'Do timed Section 4 practice: play the audio once only, then check your answers',
  ],
  backPath: '/exams/english/#listening',
  backLabel: 'Back to Listening',
  aiContext: 'Academic Lectures for IELTS Listening Section 4: covers understanding the monologue format, recognising signpost language (15+ phrases by function), handling academic vocabulary, and distinguishing main ideas from supporting details in university-style lectures.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...academicLecturesData,
      title: academicLecturesData.title + ' (Foundations)',
      overview: { ...academicLecturesData.overview, duration: '15-20 minutes' },
      sections: academicLecturesData.sections.slice(0, Math.max(2, Math.ceil(academicLecturesData.sections.length / 2))),
      exercises: academicLecturesData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...academicLecturesData,
      title: academicLecturesData.title + ' (Advanced)',
      overview: { ...academicLecturesData.overview, duration: '30-35 minutes' },
      sections: academicLecturesData.sections,
      exercises: academicLecturesData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(academicLecturesData.tips || []),
        'For Band 8+: Train yourself to understand speakers at fast natural speed.',
        'Advanced tip: Practice with varied accents and complex academic content.',
      ],
    };
  }
  return academicLecturesData;
}
